import React, { useContext, useEffect, useState } from 'react';
import { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { Col, Row } from 'reactstrap';
import { LabelledSelectFormHook } from '../../../../../../components/LabelledSelectFormHook';
import { SelectOption } from '../../../../../../components/Select';
import { DepartmentService } from '../../../../../../services/applicant_tracking/DepartmentService';
import { CreateJobDto } from '../../../../../../services/applicant_tracking/JobService/CreateJobDto';
import { SetupJobContext } from '../../index';
import { IconButton } from '../../../../../../components/IconButton';
import { CreateDepartmentModal } from './CreateDepartmentModal';
import { Alert, AlertType } from '../../../../../../components/Alert';

interface PropTypes {
  formWatch: UseFormWatch<CreateJobDto>;
  formControl: Control<CreateJobDto>;
  setValue: UseFormSetValue<CreateJobDto>;
}

export async function loadDepartmentOptions(): Promise<SelectOption[]> {
  return (await DepartmentService.list()).map((dep) => ({
    value: dep.id.toString(),
    label: dep.name,
  }));
}

export function DepartmentInput(props: PropTypes) {
  const setupJobContext = useContext(SetupJobContext);
  const required = true;

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [departmentOptions, setDepartmentOptions] = useState<SelectOption[]>(
    [],
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedDepartment, setSelectedDepartment] =
    useState<SelectOption>(null);
  const [isAlertVisible, setIsAlertVisible] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<AlertType>(AlertType.Success);
  const [alertMessage, setAlertMessage] = useState<JSX.Element>(null);
  const jobRequisitionId = props.formWatch('jobRequisitionId');

  useEffect(() => {
    loadDepartmentOptions()
      .then(setDepartmentOptions)
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    if (selectedDepartment) {
      props.setValue('departmentId', selectedDepartment.value, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [selectedDepartment]);

  return (
    <>
      {isModalOpen && (
        <CreateDepartmentModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          setSelectedDepartment={setSelectedDepartment}
          setIsLoading={setIsLoading}
          setDepartmentOptions={setDepartmentOptions}
          setIsAlertVisible={setIsAlertVisible}
          setAlertType={setAlertType}
          setAlertMessage={setAlertMessage}
        />
      )}
      {isAlertVisible && (
        <Alert
          type={alertType}
          clearable={true}
          autoClearTimeout={4000}
          onClose={() => setIsAlertVisible(false)}
        >
          {alertMessage}
        </Alert>
      )}
      <Row className='mt-4'>
        <Col xs={3}>
          <LabelledSelectFormHook<CreateJobDto>
            label={'Department'}
            placeholder={'Select Department'}
            formHookContext={{
              controllerName: 'departmentId',
              formControl: props.formControl,
              required: required,
            }}
            options={departmentOptions}
            disabled={
              setupJobContext.jobRequisition != null && !!jobRequisitionId
            }
            isLoading={isLoading}
          />
        </Col>
        <Col xs={3} className='ps-0' style={{ marginTop: 'auto' }}>
          <IconButton
            buttonText='Create Department'
            iconName='bi-plus-circle'
            color='borderless'
            onClick={() => setIsModalOpen(true)}
            disabled={
              setupJobContext.jobRequisition != null && !!jobRequisitionId
            }
          />
        </Col>
      </Row>
    </>
  );
}
